import type { Project, UserProject } from "@/types"

export enum BackendBillingPlanName {
  Unknown = "unknown",

  // rev3
  Free = "free_v3",
  Pro = "pro_v3",
  Enterprise = "enterprise_v3",

  // legacy (rev1/rev2)
  LegacyFree = "free",
  LegacyHobby = "hobby",
  LegacyPro = "pro",
  LegacyProject = "project",
  LegacyNetwork = "network",
  LegacyEnterprise = "enterprise",
}

export enum ActiveBillingPlans {
  Free = "Free",
  Pro = "Pro",
  Enterprise = "Enterprise",
}

export enum BillingPeriod {
  Unknown = "unknown",
  Monthly = "monthly",
  Yearly = "yearly",
}

// Subscription status for Iroh Services projects. It's a stripe SubscriptionStatus,
// but with additional `unknown` variant
// see: https://docs.stripe.com/api/subscriptions/object#subscription_object-status
export enum SubscriptionStatus {
  incomplete = "incomplete",
  incomplete_expired = "incomplete_expired",
  trialing = "trialing",
  active = "active",
  past_due = "past_due",
  canceled = "canceled",
  unpaid = "unpaid",
  paused = "paused",
  unknown = "unknown", // not a valid stripe enumeration. Exists on Iroh Services side
  free = "free", // not a valid stripe enumeration. Exists on Iroh Services side.
}

export enum PlanMigrationStatus {
  Pending = "pending",
  Accepted = "accepted",
  Declined = "declined",
  Cancelled = "cancelled",
}

export type Subscription = {
  id: string
  name: string
  status: SubscriptionStatus
  period: BillingPeriod
  trial_end?: number
  items: SubscriptionItem[]
}

export type SubscriptionItem = {
  id: string
  metadata: Record<string, string>
  nickname: string
  recurring: {
    aggregate_usage: null
    interval: "month"
    interval_count: number
    meter: null
    trial_period_days: null
    usage_type: "licensed"
  }
  // tiers: null;
  // tiers_mode: null;
  // transform_quantity: null;
  quantity: number
  unit_amount: number
  unit_amount_decimal: string
}

export function canCancelSubscription(status: SubscriptionStatus): boolean {
  return (
    status === SubscriptionStatus.active ||
    status === SubscriptionStatus.trialing
  )
}

export type BillingDetails = {
  plan: BackendBillingPlanName
  period: BillingPeriod
  payment_method?: string
}

export type BillingPlanUsage = {
  blob_count: number
  blob_size: number
  max_blob_size?: number
  included_blob_size: number
}

export type PromoCodeInfo = {
  code: string
  stripe_id: string
  metadata: Record<string, string>
}

export enum EntitlementType {
  Base = "base",
  Relays = "relays",
  RelayConnections = "relay_connections",
  Metrics = "metrics",
  MaxEndpoints = "max_endpoints",
  Retention = "retention",
  Endpoints = "endpoints",
  Collaboration = "collaboration",
  Regions = "regions",
  MultiCloud = "multi_cloud",
  Dns = "dns",
  Workflows = "workflows",
  Support = "support",
  Services = "services",
  SLASupport = "sla",
  NetDiagnostics = "net_diagnostics",
  Tickets = "tickets",
  MultiRelayPerRegion = "multi_relay_per_region",
}

export function isLegacyNetworkPlan(project: UserProject): boolean {
  return project.billing_plan === BackendBillingPlanName.LegacyNetwork
}

export function isLegacyProPlan(project: UserProject): boolean {
  return project.billing_plan === BackendBillingPlanName.LegacyPro
}

export function isFreePlan(project: UserProject): boolean {
  return (
    project.billing_plan === BackendBillingPlanName.Free ||
    project.billing_plan === BackendBillingPlanName.LegacyFree
  )
}

export function isProPlan(project: UserProject): boolean {
  return (
    project.billing_plan === BackendBillingPlanName.Pro ||
    project.billing_plan === BackendBillingPlanName.LegacyHobby
  )
}

export function isEnterprisePlan(project: UserProject): boolean {
  return (
    project.billing_plan === BackendBillingPlanName.Enterprise ||
    project.billing_plan === BackendBillingPlanName.LegacyEnterprise
  )
}

/// Rough categorization of payment states
export enum PaymentState {
  // Unpaid, cancelled, paused, incomplete_expired
  Bad = 0,
  // Incomplete, past due, unknown
  Incomplete = 1,
  // Active, trialing
  Good = 2,
}

// see https://docs.stripe.com/api/subscriptions/object#subscription_object-status
export const PAYMENT_STATES: Record<SubscriptionStatus, PaymentState> = {
  [SubscriptionStatus.incomplete]: PaymentState.Incomplete,
  [SubscriptionStatus.incomplete_expired]: PaymentState.Bad,
  [SubscriptionStatus.trialing]: PaymentState.Good,
  [SubscriptionStatus.active]: PaymentState.Good,
  [SubscriptionStatus.past_due]: PaymentState.Incomplete,
  [SubscriptionStatus.canceled]: PaymentState.Bad,
  [SubscriptionStatus.unpaid]: PaymentState.Bad,
  [SubscriptionStatus.paused]: PaymentState.Bad,
  [SubscriptionStatus.unknown]: PaymentState.Incomplete,
  [SubscriptionStatus.free]: PaymentState.Good,
}

export function paymentState(state: SubscriptionStatus): PaymentState {
  return PAYMENT_STATES[state]
}

// Returns true if the state of the subscription is Notice or Good
//
// i.e. returns true if the subscription is active, trialing, or in an incomplete-but-recoverable state
// (due but not overdue, incomplete but not incomplete-expired)
export function paymentOk(state: SubscriptionStatus): boolean {
  return PAYMENT_STATES[state] >= PaymentState.Incomplete
}

// Returns true if the project has the entitlement.
//
// If requirePayment is set, the subscription must have at least that payment level.
// If not set, payment of the subscription is not checked.
export function hasEntitlement(
  project: UserProject | Project | undefined,
  item: EntitlementType,
  minimalPaymentState: PaymentState = PaymentState.Good,
) {
  const list = project?.entitlements
  if (!list || !list.length) return false
  if (!list.includes(item)) return false
  return hasPaymentState(project, minimalPaymentState)
}

export function hasPaymentState(
  project: UserProject | Project | undefined,
  minimalPaymentState: PaymentState = PaymentState.Good,
) {
  if (!project) return false
  if (
    project.billing_plan === BackendBillingPlanName.Free ||
    project.billing_plan === BackendBillingPlanName.LegacyFree
  )
    return true
  return paymentState(project.subscription_status) >= minimalPaymentState
}

export function hasEntitlementUnpaid(
  project: UserProject | Project | undefined,
  item: EntitlementType,
) {
  const list = project?.entitlements
  if (!list || !list.length) return false
  if (!list.includes(item)) return false
  return true
}

// Maps legacy backend plan names to their v3 equivalents for display purposes
const legacyPlanMapping: Partial<
  Record<BackendBillingPlanName, BackendBillingPlanName>
> = {
  [BackendBillingPlanName.LegacyFree]: BackendBillingPlanName.Free,
  [BackendBillingPlanName.LegacyHobby]: BackendBillingPlanName.Pro,
  [BackendBillingPlanName.LegacyEnterprise]: BackendBillingPlanName.Enterprise,
}

// Legacy paid plans that don't map cleanly to a current tier. Displayed as a
// generic "Legacy" badge so they aren't mislabeled as "Free".
const legacyPaidPlans: BackendBillingPlanName[] = [
  BackendBillingPlanName.LegacyPro,
  BackendBillingPlanName.LegacyProject,
  BackendBillingPlanName.LegacyNetwork,
]

export function userProjectPlan(project: UserProject): PlanDetails {
  if (
    legacyPaidPlans.includes(project.billing_plan as BackendBillingPlanName)
  ) {
    return { ...proPlan, name: "Legacy" }
  }
  const planName =
    legacyPlanMapping[project.billing_plan as BackendBillingPlanName] ??
    project.billing_plan
  return planDetails.find((plan) => plan.plan === planName) || freePlan
}

const ANNUAL_DISCOUNT_PCT = 0.17

/** Rev3 pricing constants – single source of truth for calculator & display */
export const rev3Pricing = {
  proBase: 19, // $/month
  includedEndpoints: 100, // included in base
  endpointRate: 0.5, // per 100 extra endpoints
  metricsRate: 1.49, // per 1K DPM
  relayHourlyRate: 0.27, // per relay per hour
  hoursPerMonth: 730, // hours used to estimate monthly relay cost
  includedDpm: 10_000, // DPM included in pro
  freeDpm: 1_000, // DPM included in free
  freeEndpoints: 10, // endpoints included in free
} as const

export type PlanDetails = {
  plan: BackendBillingPlanName
  id: string
  name: string
  description: string
  enabled: boolean
  popular: boolean
  price: Record<BillingPeriod, number | string>
  href: string
  buttonLabel: string
  featuresNote?: string
  features: { title: string; description?: string }[]
  color: "green" | "green-darker" | "purple"
}

export const freePlan: PlanDetails = {
  enabled: true,
  plan: BackendBillingPlanName.Free,
  name: "Free",
  id: "tier-free",
  href: `/signup?redirect=${BackendBillingPlanName.Free}`,
  popular: false,
  price: {
    [BillingPeriod.Unknown]: 0,
    [BillingPeriod.Monthly]: 0,
    [BillingPeriod.Yearly]: 0,
  },
  description: "For local development and testing.",
  features: [
    { title: "All features in Pro, limited" },
    { title: "7 day retention" },
    { title: "Community support" },
  ],
  color: "green",
  buttonLabel: "Get Started",
}

export const proPlan: PlanDetails = {
  enabled: true,
  plan: BackendBillingPlanName.Pro,
  name: "Pro",
  id: "tier-hobby",
  popular: true,
  href: `/signup?redirect=${BackendBillingPlanName.Pro}`,
  price: {
    [BillingPeriod.Unknown]: -1,
    [BillingPeriod.Monthly]: rev3Pricing.proBase,
    [BillingPeriod.Yearly]: Math.round(
      rev3Pricing.proBase * 12 * (1 - ANNUAL_DISCOUNT_PCT),
    ),
  },
  description: "For shipping your app to prod.",
  features: [
    { title: "Pay as you go pricing" },
    { title: "30 day retention" },
    { title: "8x5 support tickets" },
  ],
  color: "green",
  buttonLabel: "Free trial",
}

export const enterprisePlan: PlanDetails = {
  enabled: true,
  popular: false,
  plan: BackendBillingPlanName.Enterprise,
  name: "Enterprise",
  id: "tier-enterprise",
  href: "https://cal.com/team/number-0/iroh-services",
  description: "For large-scale deployments.",
  price: {
    [BillingPeriod.Unknown]: "Contact Us",
    [BillingPeriod.Monthly]: "Contact Us",
    [BillingPeriod.Yearly]: "Contact Us",
  },
  features: [
    { title: "On-prem and multi-cloud" },
    { title: "Custom retention" },
    { title: "SLAs" },
    { title: "Support Engineer" },
  ],
  color: "green",
  buttonLabel: "Let's Chat",
}

export const planDetails: PlanDetails[] = [freePlan, proPlan, enterprisePlan]

export type PricingDeepDiveTier = {
  plan: ActiveBillingPlans
  title: string
  popular?: boolean
  headline: string
  headlineNote?: string
  features: string[]
  buttonLabel: string
  href: string
}

export type PricingDeepDiveSection = {
  id: string
  title: string
  subtitle?: string
  tiers: PricingDeepDiveTier[]
}

export const pricingDeepDiveSections: PricingDeepDiveSection[] = [
  // TODO: re-enable observability section
  // {
  //   id: "metrics",
  //   title: "Observability",
  //   subtitle: "Fully-managed, highly-scalable metrics & observability service.",
  //   tiers: [
  //     {
  //       plan: ActiveBillingPlans.Free,
  //       title: "Free",
  //       headline: "$0",
  //       headlineNote: "No credit card required",
  //       features: [
  //         "1k data points",
  //         "10 custom metrics",
  //         "1 day retention",
  //         "Community support",
  //       ],
  //       buttonLabel: "Get Started",
  //       href: `/signup?redirect=${BackendBillingPlanName.Free}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Pro,
  //       title: "Pro",
  //       popular: true,
  //       headline: "$1.49",
  //       headlineNote: "per 1k data points",
  //       features: [
  //         "10k data points then pay as you go",
  //         "Unlimited custom metrics",
  //         "7 day retention",
  //         "Business hours support",
  //       ],
  //       buttonLabel: "Start Free Trial",
  //       href: `/signup?redirect=${BackendBillingPlanName.Pro}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Enterprise,
  //       title: "Enterprise",
  //       headline: "As low as $1",
  //       headlineNote: "per 1k data points",
  //       features: [
  //         "Volume discounts available",
  //         "Custom retention",
  //         "On-prem available",
  //         "Dedicated support engineer",
  //       ],
  //       buttonLabel: "Contact Us",
  //       href: "https://cal.com/team/number-0/iroh-services",
  //     },
  //   ],
  // },
  // {
  //   id: "relays",
  //   title: "Hosting",
  //   subtitle:
  //     "Relay servers route traffic when direct connections aren't possible",
  //   tiers: [
  //     {
  //       plan: ActiveBillingPlans.Free,
  //       title: "Free",
  //       headline: "$0",
  //       headlineNote: "public multi-tenant",
  //       features: ["Shared infrastructure", "Best-effort availability"],
  //       buttonLabel: "Get Started",
  //       href: `/signup?redirect=${BackendBillingPlanName.Free}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Pro,
  //       title: "Pro",
  //       popular: true,
  //       headline: "$199",
  //       headlineNote: "per dedicated relay",
  //       features: ["60k concurrent connections", "Multi-region available"],
  //       buttonLabel: "Start Free Trial",
  //       href: `/signup?redirect=${BackendBillingPlanName.Pro}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Enterprise,
  //       title: "Enterprise",
  //       headline: "Custom",
  //       features: [
  //         "On-prem and multi-cloud",
  //         "Scalable connection limits",
  //         "SLA-backed uptime",
  //       ],
  //       buttonLabel: "Contact Us",
  //       href: "https://cal.com/team/number-0/iroh-services",
  //     },
  //   ],
  // },
  // {
  //   id: "support",
  //   title: "Support",
  //   subtitle: "Get help when you need it, at the level your team requires",
  //   tiers: [
  //     {
  //       plan: ActiveBillingPlans.Free,
  //       title: "Free",
  //       headline: "Community",
  //       features: ["GitHub Issues", "Discord community"],
  //       buttonLabel: "Get Started",
  //       href: `/signup?redirect=${BackendBillingPlanName.Free}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Pro,
  //       title: "Pro",
  //       popular: true,
  //       headline: "Business Hours",
  //       features: ["Faster response times", "8x5 support tickets"],
  //       buttonLabel: "Start Free Trial",
  //       href: `/signup?redirect=${BackendBillingPlanName.Pro}`,
  //     },
  //     {
  //       plan: ActiveBillingPlans.Enterprise,
  //       title: "Enterprise",
  //       headline: "Custom",
  //       features: ["Dedicated support engineer", "Custom SLAs", "Code reviews"],
  //       buttonLabel: "Contact Us",
  //       href: "https://cal.com/team/number-0/iroh-services",
  //     },
  //   ],
  // },
]

/** Free plan's max metrics retention window in milliseconds. */
export const FREE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
/** Pro plan's max metrics retention window; anything longer needs Enterprise. */
export const PRO_RETENTION_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

/** Maximum metrics history window in milliseconds, per plan. undefined = unlimited. */
export function metricsRetentionMs(
  project: UserProject | Project,
): number | undefined {
  if (isFreePlan(project)) return FREE_RETENTION_MS
  if (isProPlan(project)) return PRO_RETENTION_MS
  return undefined // enterprise / unknown — no limit
}

/** Minimum metrics interval in milliseconds, per plan. undefined = no restriction. */
export function metricsMinIntervalMs(
  project: UserProject | Project,
): number | undefined {
  if (isFreePlan(project) || isProPlan(project)) return 60 * 1000 // 1 minute
  return undefined // enterprise — sub-minute allowed
}

export const featureInfo: Record<
  EntitlementType,
  { name: string; description: string } | null
> = {
  [EntitlementType.Base]: null,
  [EntitlementType.Endpoints]: null,
  [EntitlementType.Metrics]: {
    name: "Data Points per Minute",
    description: "Fully-managed, highly-scalable metrics service.",
  },
  [EntitlementType.MaxEndpoints]: {
    name: "Concurrent Endpoints",
    description:
      "Maximum number of endpoints that can report metrics simultaneously.",
  },
  [EntitlementType.Relays]: {
    name: "Relays",
    description: "Relay servers for NAT traversal and connection brokering.",
  },
  [EntitlementType.RelayConnections]: {
    name: "Concurrent Connections per Relay",
    description: "Maximum simultaneous connections each relay can handle.",
  },
  [EntitlementType.Regions]: {
    name: "Multi-region",
    description:
      "Deploy relays across multiple geographic regions for lower latency.",
  },
  [EntitlementType.Dns]: {
    name: "Dedicated DNS",
    description: "Custom DNS configuration for your infrastructure.",
  },
  [EntitlementType.Collaboration]: {
    name: "Collaborators",
    description: "Team members who can access and manage your project.",
  },
  [EntitlementType.Support]: {
    name: "Community Support via Github & Discord",
    description: "Access to community forums and open source issue tracking.",
  },
  [EntitlementType.SLASupport]: {
    name: "SLAs",
    description:
      "Service level agreements with guaranteed uptime and response times.",
  },
  [EntitlementType.Workflows]: {
    name: "Diagnostics",
    description: "Tools to debug and monitor your network connections.",
  },
  [EntitlementType.Services]: {
    name: "Dedicated Support Engineer",
    description:
      "Direct access to an engineer for architecture and troubleshooting.",
  },
  [EntitlementType.NetDiagnostics]: {
    name: "Diagnostics",
    description: "Tools to debug and monitor your network connections.",
  },
  [EntitlementType.Retention]: {
    name: "Retention",
    description: "How long metrics and logs are stored.",
  },
  [EntitlementType.Tickets]: null,
  [EntitlementType.MultiCloud]: {
    name: "Multi-cloud",
    description:
      "Deploy across multiple cloud providers (AWS, GCP, Azure, etc.).",
  },
  [EntitlementType.MultiRelayPerRegion]: null,
}

export const featureGrid: Record<
  EntitlementType,
  Record<ActiveBillingPlans, { title: string; description?: string }> | null
> = {
  [EntitlementType.Base]: null,
  [EntitlementType.Endpoints]: null,
  [EntitlementType.Metrics]: {
    [ActiveBillingPlans.Free]: {
      title: `${rev3Pricing.freeDpm / 1_000}K DPM`,
      description: `${rev3Pricing.freeDpm / rev3Pricing.freeEndpoints} metrics × ${rev3Pricing.freeEndpoints} endpoints`,
    },
    [ActiveBillingPlans.Pro]: {
      title: `${rev3Pricing.includedDpm / 1_000}K DPM`,
      description: `then $${rev3Pricing.metricsRate}/1K DPM`,
    },
    [ActiveBillingPlans.Enterprise]: {
      title: "Custom",
      description: "Volume discounts",
    },
  },
  [EntitlementType.MaxEndpoints]: {
    [ActiveBillingPlans.Free]: { title: `${rev3Pricing.freeEndpoints}` },
    [ActiveBillingPlans.Pro]: {
      title: `${rev3Pricing.includedEndpoints}`,
      description: `then $${rev3Pricing.endpointRate}/100 endpoints`,
    },
    [ActiveBillingPlans.Enterprise]: { title: "Custom", description: "" },
  },
  [EntitlementType.Retention]: {
    [ActiveBillingPlans.Free]: { title: "7 days" },
    [ActiveBillingPlans.Pro]: { title: "30 days" },
    [ActiveBillingPlans.Enterprise]: { title: "Custom", description: "" },
  },
  [EntitlementType.Relays]: {
    [ActiveBillingPlans.Free]: { title: "Public", description: "Multi-tenant" },
    [ActiveBillingPlans.Pro]: {
      title: "Cloud",
      description: `$${rev3Pricing.relayHourlyRate}/relay/hour`,
    },
    [ActiveBillingPlans.Enterprise]: {
      title: "Custom",
      description: "",
    },
  },
  [EntitlementType.RelayConnections]: {
    [ActiveBillingPlans.Free]: {
      title: "Variable",
      description: "Rate-limited",
    },
    [ActiveBillingPlans.Pro]: { title: "60k" },
    [ActiveBillingPlans.Enterprise]: { title: "Custom" },
  },
  [EntitlementType.Collaboration]: {
    [ActiveBillingPlans.Free]: { title: "One user" },
    [ActiveBillingPlans.Pro]: { title: "Unlimited users", description: "" },
    [ActiveBillingPlans.Enterprise]: {
      title: "Unlimited users",
      description: "",
    },
  },
  [EntitlementType.Regions]: {
    [ActiveBillingPlans.Free]: { title: "", description: "" },
    [ActiveBillingPlans.Pro]: { title: "Available", description: "" },
    // [ActiveBillingPlans.LegacyPro]: { title: "Available", description: "" },
    [ActiveBillingPlans.Enterprise]: { title: "Custom", description: "" },
  },
  [EntitlementType.Dns]: {
    [ActiveBillingPlans.Free]: { title: "", description: "" },
    [ActiveBillingPlans.Pro]: { title: "", description: "" },
    // [ActiveBillingPlans.LegacyPro]: { title: "Available", description: "" },
    [ActiveBillingPlans.Enterprise]: { title: "Available", description: "" },
  },
  [EntitlementType.Services]: {
    [ActiveBillingPlans.Free]: { title: "", description: "" },
    [ActiveBillingPlans.Pro]: { title: "", description: "" },
    // [ActiveBillingPlans.LegacyPro]: { title: "", description: "" },
    [ActiveBillingPlans.Enterprise]: { title: "Available", description: "" },
  },
  [EntitlementType.Support]: {
    [ActiveBillingPlans.Free]: { title: "Available", description: "" },
    [ActiveBillingPlans.Pro]: { title: "Available", description: "" },
    // [ActiveBillingPlans.LegacyPro]: {
    //   title: "Available",
    //   description: "",
    // },
    [ActiveBillingPlans.Enterprise]: { title: "Available", description: "" },
  },
  [EntitlementType.SLASupport]: {
    [ActiveBillingPlans.Free]: { title: "", description: "" },
    [ActiveBillingPlans.Pro]: { title: "", description: "" },
    // [ActiveBillingPlans.LegacyPro]: {
    //   title: "Priority Business Hours",
    //   description: "",
    // },
    [ActiveBillingPlans.Enterprise]: { title: "Custom", description: "" },
  },
  [EntitlementType.Workflows]: {
    [ActiveBillingPlans.Free]: { title: "", description: "" },
    [ActiveBillingPlans.Pro]: { title: "", description: "" },
    // [ActiveBillingPlans.LegacyPro]: { title: "", description: "" },
    [ActiveBillingPlans.Enterprise]: { title: "Available", description: "" },
  },
  [EntitlementType.NetDiagnostics]: {
    [ActiveBillingPlans.Free]: { title: "3 reports" },
    [ActiveBillingPlans.Pro]: {
      title: "10 reports",
      description: "then pay as you go",
    },
    [ActiveBillingPlans.Enterprise]: {
      title: "Custom",
      description: "Volume discounts",
    },
  },
  [EntitlementType.Tickets]: null,
  [EntitlementType.MultiCloud]: {
    [ActiveBillingPlans.Free]: { title: "" },
    [ActiveBillingPlans.Pro]: { title: "" },
    [ActiveBillingPlans.Enterprise]: { title: "Available" },
  },
  [EntitlementType.MultiRelayPerRegion]: null,
}

export const featureSections: { name: string; features: EntitlementType[] }[] =
  [
    {
      name: "Platform",
      features: [EntitlementType.Collaboration, EntitlementType.NetDiagnostics],
    },
    {
      name: "Metrics",
      features: [
        EntitlementType.Metrics,
        EntitlementType.Retention,
        EntitlementType.MaxEndpoints,
      ],
    },
    {
      name: "Hosting",
      features: [
        EntitlementType.Relays,
        EntitlementType.RelayConnections,
        EntitlementType.Regions,
        EntitlementType.MultiCloud,
        EntitlementType.Dns,
      ],
    },
    {
      name: "Support",
      features: [
        EntitlementType.Support,
        EntitlementType.SLASupport,
        EntitlementType.Services,
      ],
    },
  ]
